import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

import { AssetFeed } from '../components/assets/AssetFeed';
import { CalendarGemBadge } from '../components/calendar/CalendarGemBadge';
import { HomeCollectionStatsBanner } from '../components/HomeCollectionStatsBanner';
import { OffscreenHomeView } from '../components/OffscreenHomeView';
import { PendingCountBadge } from '../components/PendingCountBadge';
import { useActiveSubject } from '../context/ActiveSubjectContext';
import { useAuth } from '../context/AuthContext';
import { useProductStudyMode } from '../context/ProductStudyModeContext';
import {
  reviewService,
  type DailyReviewProgress,
} from '../services/reviewService';
import {
  buildCalendarGemDays,
  resolveTodayCalendarGemTier,
  type CalendarDayGem,
} from '../utils/calendarGemDays';
import { getReviewProductDayKey } from '../utils/reviewProductDay';
import { getDefaultStreamForSubject, isCoreSubjectCode } from '../utils/subjectConfig';

export function HomePage() {
  const { user } = useAuth();
  const { activeSubject } = useActiveSubject();
  const { productMode } = useProductStudyMode();
  const location = useLocation();
  const [reviewTodayKey, setReviewTodayKey] = useState(() => getReviewProductDayKey());
  const [dailyReviewProgress, setDailyReviewProgress] = useState<DailyReviewProgress | null>(null);
  const [dayGems, setDayGems] = useState<Record<string, CalendarDayGem>>({});
  const [visibilityEpoch, setVisibilityEpoch] = useState(0);
  const statsRefreshKey = `${location.key}:${activeSubject}:${visibilityEpoch}`;
  const reviewAvailable = isCoreSubjectCode(activeSubject);
  const isOffscreenHome = productMode === 'offscreen';

  const refreshReviewDayState = useCallback(async (options?: { forceProgress?: boolean }) => {
    if (isOffscreenHome || !user?.id || !isCoreSubjectCode(activeSubject)) {
      setDailyReviewProgress(null);
      return null;
    }

    const { rolled } = await reviewService.handleReviewProductDayRollover({
      userId: user.id,
      subject: activeSubject,
      activeStream: getDefaultStreamForSubject(activeSubject),
    });
    setReviewTodayKey(getReviewProductDayKey());

    const forceProgress = options?.forceProgress === true || rolled;
    if (!forceProgress) {
      const cached = reviewService.peekDailyReviewProgress(user.id);
      if (cached) {
        setDailyReviewProgress(cached);
        return cached;
      }
    }

    try {
      const nextProgress = await reviewService.loadDailyReviewProgress(user.id, {
        forceRefresh: forceProgress,
        peekMissingSurfaces: true,
      });
      setDailyReviewProgress(nextProgress);
      return nextProgress;
    } catch {
      setDailyReviewProgress(null);
      return null;
    }
  }, [activeSubject, isOffscreenHome, user?.id]);

  const loadCalendarGems = useCallback(async (progressOverride?: DailyReviewProgress | null) => {
    if (isOffscreenHome || !user?.id) {
      return;
    }

    try {
      const history = await reviewService.loadSessionHistory(user.id, { limit: 180 });
      const progress = progressOverride ?? dailyReviewProgress;
      setDayGems(buildCalendarGemDays(history, {
        todayKey: reviewTodayKey,
        todayGemTier: resolveTodayCalendarGemTier(progress),
      }));
    } catch {
      setDayGems({});
    }
  }, [dailyReviewProgress, isOffscreenHome, reviewTodayKey, user?.id]);

  useEffect(() => {
    if (isOffscreenHome) return;
    void refreshReviewDayState({ forceProgress: true });
  }, [refreshReviewDayState, location.key, activeSubject, isOffscreenHome]);

  useEffect(() => {
    if (isOffscreenHome) return;
    void loadCalendarGems(dailyReviewProgress);
  }, [dailyReviewProgress, loadCalendarGems, isOffscreenHome]);

  useEffect(() => {
    if (isOffscreenHome) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setVisibilityEpoch((current) => current + 1);
        void refreshReviewDayState({ forceProgress: true });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isOffscreenHome, refreshReviewDayState]);

  if (isOffscreenHome) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <OffscreenHomeView />
      </div>
    );
  }

  const reviewPendingCount = reviewAvailable ? (dailyReviewProgress?.totalPendingCount ?? 0) : 0;
  const todayDayGem = dayGems[reviewTodayKey] ?? null;

  return (
    <div className="space-y-5 pt-4">
      <section className="space-y-3 px-1">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            {todayDayGem ? (
              <CalendarGemBadge seed={todayDayGem.seed} tier={todayDayGem.tier} size={20} />
            ) : null}
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-ink sm:text-2xl">今日学习</h1>
            </div>
          </div>
          {reviewAvailable ? (
            <Link
              to="/review/session"
              className="relative inline-flex items-center gap-2 rounded-full border border-primary/20 bg-[#EAF2FF] px-4 py-2 text-sm font-medium text-primary-strong"
            >
              <Sparkles size={16} />
              开始复习
              {reviewPendingCount > 0 ? (
                <PendingCountBadge
                  count={reviewPendingCount}
                  className="absolute -right-1 -top-1"
                />
              ) : null}
            </Link>
          ) : (
            <span
              className="relative inline-flex cursor-not-allowed items-center gap-2 rounded-full border border-border bg-canvas px-4 py-2 text-sm font-medium text-muted opacity-60"
              title="其他学科不支持复习流"
              aria-disabled="true"
            >
              <Sparkles size={16} />
              开始复习
            </span>
          )}
        </div>

        {user?.id ? (
          <HomeCollectionStatsBanner userId={user.id} refreshKey={statsRefreshKey} />
        ) : null}
      </section>

      <AssetFeed variant="home" showSearch={false} dayGems={dayGems} />
    </div>
  );
}
